import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import QuranService from '../../services/quranService';
import { createAssignment } from '../../services/assignmentService';
import * as DatabaseUtil from '../../utils/databaseUtil';
import VersePreview from '../../components/VersePreview';
import '../../styles/CreateAssignmentPage.css';

const CreateAssignmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  // Get any passed params from location state
  const initialStudentId = location.state?.studentId || null;
  const initialStudentName = location.state?.studentName || null;
  const initialHalaqaId = location.state?.halaqaId || null;
  const initialHalaqaName = location.state?.halaqaName || null;
  
  // Check if we're in edit mode
  const isEditMode = location.state?.isEditMode || false;
  const assignmentId = location.state?.assignmentId || null;
  
  console.log('CreateAssignmentPage - Mode:', isEditMode ? 'Edit' : 'Create');
  console.log('Assignment ID:', assignmentId);
  
  // State for general UI
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  
  // State for verse preview
  const [showVersePreview, setShowVersePreview] = useState(false);
  const [loadingVerses, setLoadingVerses] = useState(false);
  const [verses, setVerses] = useState([]);
  const [teachingTips, setTeachingTips] = useState([]);
  
  // State for dropdowns
  const [showSurahDropdown, setShowSurahDropdown] = useState(false);
  const [showStudentsDropdown, setShowStudentsDropdown] = useState(false);
  const [searchText, setSearchText] = useState('');
  
  // Assignment data
  const [assignmentType, setAssignmentType] = useState('new-memorization');
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [fromVerse, setFromVerse] = useState('1');
  const [toVerse, setToVerse] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Set due date to 3 days from now by default
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  });
  const [matchingQuiz, setMatchingQuiz] = useState(false);
  
  // Surah options
  const [surahOptions, setSurahOptions] = useState([]);
  const [loadingAssignment, setLoadingAssignment] = useState(isEditMode);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load Surahs and Halaqa data on init
  useEffect(() => {
    // Initialize database for teaching tips
    const initDb = async () => {
      await DatabaseUtil.initDatabase();
    };
    
    initDb();
    
    // Load surahs
    const surahs = QuranService.getAllSurahs();
    setSurahOptions(surahs);
    
    // Fetch classes/halaqas
    const fetchClasses = async () => {
      try {
        const response = await api.get('/halaqas');
        const classesData = response.data.success 
          ? response.data.data 
          : response.data;
        const classesArray = Array.isArray(classesData) ? classesData : [];
        setClasses(classesArray);
        
        // If we have an initial halaqaId from params, select it
        if (initialHalaqaId) {
          // Find the halaqa in the array to get its details
          const selectedHalaqa = classesArray.find(c => c._id === initialHalaqaId);
          if (selectedHalaqa) {
            // Fetch students for this halaqa
            fetchStudentsForHalaqa(initialHalaqaId);
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };
    
    fetchClasses();
    
    // If we have initialStudentId, initialize selectedStudents
    if (initialStudentId && initialStudentName) {
      setSelectedStudents([{
        id: initialStudentId,
        name: initialStudentName
      }]);
    }
  }, [initialHalaqaId, initialStudentId, initialStudentName]);
  
  // Store assignment data for student processing in a separate step
  const [fetchedAssignmentData, setFetchedAssignmentData] = useState(null);
  
  // Step 1: Fetch assignment data
  useEffect(() => {
    // Only fetch assignment data when in edit mode with a valid assignmentId
    if (isEditMode && assignmentId) {
      const fetchAssignmentData = async () => {
        try {
          setLoadingAssignment(true);
          
          // Import the necessary service method
          const { getAssignmentById } = await import('../../services/assignmentService');
          
          const assignmentData = await getAssignmentById(assignmentId);
          console.log('Assignment data received:', assignmentData);
          
          // Populate form with assignment data
          setAssignmentType(assignmentData.type || 'new-memorization');
          
          if (assignmentData.surah) {
            // Find the surah in our options
            const surah = surahOptions.find(s => s.id === assignmentData.surah.id);
            setSelectedSurah(surah || assignmentData.surah);
          }
          
          if (assignmentData.verses) {
            setFromVerse(assignmentData.verses.from.toString());
            setToVerse(assignmentData.verses.to.toString());
          }
          
          // Set due date
          if (assignmentData.dueDate) {
            const date = new Date(assignmentData.dueDate);
            setSelectedDate(date.toISOString().split('T')[0]);
          }
          
          // Set matching quiz flag if available
          if (assignmentData.matchingQuiz !== undefined) {
            setMatchingQuiz(assignmentData.matchingQuiz);
          }
          
          // Store data for student processing
          setFetchedAssignmentData(assignmentData);
          
          // Only fetch students for this halaqa if needed
          if (assignmentData.halaqaId) {
            await fetchStudentsForHalaqa(assignmentData.halaqaId);
          }
          
        } catch (error) {
          console.error('Error fetching assignment:', error);
          window.alert('Failed to load assignment data');
        } finally {
          setLoadingAssignment(false);
        }
      };
      
      fetchAssignmentData();
    }
  }, [isEditMode, assignmentId, surahOptions]);
  
  // Step 2: Process student selection separately to avoid re-rendering loop
  useEffect(() => {
    if (fetchedAssignmentData && fetchedAssignmentData.students && fetchedAssignmentData.students.length > 0 && students.length > 0) {
      try {
        if (typeof fetchedAssignmentData.students[0] === 'object' && fetchedAssignmentData.students[0]._id) {
          // If students are objects with _id
          const selectedIds = fetchedAssignmentData.students.map(s => s._id);
          
          // Find the selected students in the students array
          const selectedStudentObjects = students.filter(student => 
            selectedIds.includes(student.id)
          );
          setSelectedStudents(selectedStudentObjects);
        } else {
          // If students are just IDs
          const selectedStudentObjects = students.filter(student => 
            fetchedAssignmentData.students.includes(student.id)
          );
          setSelectedStudents(selectedStudentObjects);
        }
      } catch (error) {
        console.error('Error processing student selection:', error);
      }
    }
  }, [fetchedAssignmentData, students]);
  
  // Function to fetch students for a halaqa
  const fetchStudentsForHalaqa = async (halaqaId) => {
    if (!halaqaId) {
      setStudents([]);
      return;
    }
    
    try {
      const response = await api.get(`/halaqas/${halaqaId}/students`);
      const studentsData = response.data.success 
        ? response.data.data 
        : response.data;
      const formattedStudents = (Array.isArray(studentsData) ? studentsData : [])
        .map(student => ({
          id: student._id,
          name: student.name
        }));
      setStudents(formattedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  // Handler for assignment type change
  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type);
    // Clear errors
    if (formErrors.assignmentType) {
      setFormErrors(prev => ({ ...prev, assignmentType: '' }));
    }
  };
  
  // Handler for surah selection
  const handleSelectSurah = (surah) => {
    setSelectedSurah(surah);
    setShowSurahDropdown(false);
    setSearchText('');
    
    // Auto-populate toVerse with the last verse of the surah
    const verseCount = QuranService.getVerseCount(surah.id);
    setToVerse(verseCount.toString());
    
    // Clear errors
    if (formErrors.surah) {
      setFormErrors(prev => ({ ...prev, surah: '' }));
    }
  };
  
  // Handler for verse input changes
  const handleVerseInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'fromVerse') {
      setFromVerse(value);
    } else if (name === 'toVerse') {
      setToVerse(value);
    }
    
    // Clear errors
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handler for halaqa (class) selection
  const handleHalaqaChange = (e) => {
    const halaqaId = e.target.value;
    
    // Fetch students for this halaqa
    fetchStudentsForHalaqa(halaqaId);
    
    // Clear selected students when changing halaqa
    setSelectedStudents([]);
    
    // Clear errors
    if (formErrors.halaqaId) {
      setFormErrors(prev => ({ ...prev, halaqaId: '' }));
    }
  };
  
  // Handler for date selection
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    
    // Clear errors
    if (formErrors.dueDate) {
      setFormErrors(prev => ({ ...prev, dueDate: '' }));
    }
  };
  
  // Handler for matching quiz toggle
  const handleMatchingQuizToggle = () => {
    setMatchingQuiz(prev => !prev);
  };
  
  // Handler for student selection
  const handleToggleStudentSelection = (student) => {
    setSelectedStudents(prev => {
      // Check if student is already selected
      const isSelected = prev.some(s => s.id === student.id);
      
      if (isSelected) {
        // Remove student
        return prev.filter(s => s.id !== student.id);
      } else {
        // Add student
        return [...prev, student];
      }
    });
    
    // Clear errors
    if (formErrors.students) {
      setFormErrors(prev => ({ ...prev, students: '' }));
    }
  };
  
  // Handler for student dropdown toggle
  const toggleStudentDropdown = () => {
    setShowStudentsDropdown(prev => !prev);
    // Close surah dropdown if open
    if (showSurahDropdown) {
      setShowSurahDropdown(false);
      setSearchText('');
    }
  };
  
  // Handler for surah search
  const handleSurahSearch = (e) => {
    setSearchText(e.target.value);
  };
  
  // Handler for clicking outside dropdowns to close them
  const handleOutsideClick = () => {
    if (showSurahDropdown) {
      setShowSurahDropdown(false);
      setSearchText('');
    }
    if (showStudentsDropdown) {
      setShowStudentsDropdown(false);
    }
  };

  // Handler for verse preview
  const handlePreviewVerses = async () => {
    if (!selectedSurah) {
      setFormErrors(prev => ({ ...prev, surah: 'Please select a Surah first' }));
      return;
    }
    
    if (!fromVerse || !toVerse) {
      setFormErrors(prev => ({ 
        ...prev, 
        fromVerse: !fromVerse ? 'Required' : '',
        toVerse: !toVerse ? 'Required' : ''
      }));
      return;
    }
    
    if (parseInt(fromVerse) > parseInt(toVerse)) {
      setFormErrors(prev => ({ 
        ...prev, 
        fromVerse: 'From verse must be less than or equal to To verse'
      }));
      return;
    }
    
    setLoadingVerses(true);
    setShowVersePreview(true);
    
    try {
      // Fetch verses
      const fetchedVerses = await QuranService.fetchVerses(
        selectedSurah.id, 
        parseInt(fromVerse), 
        parseInt(toVerse)
      );
      
      // Fetch teaching tips for these verses
      try {
        console.log(`Fetching teaching tips for Surah ${selectedSurah.id}, verses ${fromVerse}-${toVerse}...`);
        const tips = await DatabaseUtil.fetchTeachingTips(
          parseInt(selectedSurah.id),
          parseInt(fromVerse),
          parseInt(toVerse)
        );
        console.log("Teaching tips retrieved:", tips);
        console.log("Number of tips found:", Object.keys(tips).length);
        
        // Check if any tips were found
        if (Object.keys(tips).length === 0) {
          console.log("No teaching tips found for the selected verses");
        } else {
          // Log first tip as example
          const firstTipKey = Object.keys(tips)[0];
          console.log(`Example tip (${firstTipKey}):`, tips[firstTipKey]);
        }
        
        setTeachingTips(tips);
      } catch (error) {
        console.error("Error fetching teaching tips:", error);
        setTeachingTips({});
      }
      
      if (fetchedVerses) {
        setVerses(fetchedVerses);
      } else {
        // Use placeholder data if API fails
        setVerses(
          QuranService.getPlaceholderVerses(
            selectedSurah.id, 
            parseInt(fromVerse), 
            parseInt(toVerse)
          )
        );
      }
    } catch (error) {
      console.error('Error fetching verses:', error);
      // Use placeholder data if API fails
      setVerses(
        QuranService.getPlaceholderVerses(
          selectedSurah.id, 
          parseInt(fromVerse), 
          parseInt(toVerse)
        )
      );
    } finally {
      setLoadingVerses(false);
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!selectedSurah) errors.surah = 'Surah selection is required';
    if (!fromVerse.trim()) errors.fromVerse = 'From verse is required';
    if (!toVerse.trim()) errors.toVerse = 'To verse is required';
    if (selectedStudents.length === 0) errors.students = 'Please select at least one student';
    if (!selectedDate) errors.dueDate = 'Due date is required';
    
    // Validation logic
    if (fromVerse && toVerse && parseInt(fromVerse) > parseInt(toVerse)) {
      errors.fromVerse = 'From verse must be less than or equal to To verse';
    }
    
    if (selectedSurah && fromVerse && parseInt(fromVerse) < 1) {
      errors.fromVerse = 'From verse must be greater than 0';
    }
    
    if (selectedSurah && toVerse) {
      const maxVerse = QuranService.getVerseCount(selectedSurah.id);
      if (parseInt(toVerse) > maxVerse) {
        errors.toVerse = `To verse cannot exceed ${maxVerse} for ${selectedSurah.name}`;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Format the assignment data to match the expected structure
      const assignmentData = {
        title: `${selectedSurah.name} (${fromVerse}-${toVerse})`,
        description: `${assignmentType} assignment for ${selectedSurah.name}`,
        type: assignmentType,
        surah: {
          id: selectedSurah.id,
          name: selectedSurah.name
        },
        verses: {
          from: parseInt(fromVerse),
          to: parseInt(toVerse)
        },
        students: selectedStudents.map(student => student.id),
        center: user?.center || null,
        dueDate: selectedDate,
        matchingQuiz: matchingQuiz
      };
      
      // Determine if we're creating or updating
      if (isEditMode && assignmentId) {
        // Import the updateAssignment function
        const { updateAssignment } = await import('../../services/assignmentService');
        await updateAssignment(assignmentId, assignmentData);
        window.alert('Assignment updated successfully!');
      } else {
        await createAssignment(assignmentData);
        window.alert('Assignment created successfully!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} assignment:`, error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} assignment`
      }));
    } finally {
      setLoading(false);
    }
  };
  
  // Add delete assignment functionality
  const handleDeleteAssignment = async () => {
    if (!isEditMode || !assignmentId) return;
    
    if (!window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      // Import the deleteAssignment function
      const { deleteAssignment } = await import('../../services/assignmentService');
      await deleteAssignment(assignmentId);
      window.alert('Assignment deleted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || 'Failed to delete assignment'
      }));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Show loading indicator when fetching assignment for editing
  if (loadingAssignment) {
    return (
      <div className="create-assignment-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assignment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>{isEditMode ? 'Edit Assignment' : 'Create New Assignment'}</h1>
        <button 
          onClick={() => navigate(-1)} 
          className="back-btn"
        >
          ← Back
        </button>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {formErrors.submit && (
            <div className="error-message">{formErrors.submit}</div>
          )}
          
          {/* Assignment Type Section */}
          <div className="form-section">
            <h2>Assignment Type</h2>
            <div className="assignment-type-buttons">
              <button
                type="button"
                className={`type-button ${assignmentType === 'new-memorization' ? 'active' : ''}`}
                onClick={() => handleAssignmentTypeChange('new-memorization')}
              >
                <span className="type-icon">📚</span>
                <span>New Memorization</span>
              </button>
              <button
                type="button"
                className={`type-button ${assignmentType === 'revision' ? 'active' : ''}`}
                onClick={() => handleAssignmentTypeChange('revision')}
              >
                <span className="type-icon">🔄</span>
                <span>Revision</span>
              </button>
            </div>
          </div>
          
          {/* Quran Content Section */}
          <div className="form-section">
            <h2>Quran Content</h2>
            
            {/* Surah Selection */}
            <div className="form-group">
              <label>Surah</label>
              <div 
                className={`dropdown-field ${formErrors.surah ? 'error' : ''}`}
                onClick={() => {
                  setShowSurahDropdown(!showSurahDropdown);
                  setShowStudentsDropdown(false);
                }}
              >
                <span className={selectedSurah ? 'selected-text' : 'placeholder-text'}>
                  {selectedSurah 
                    ? `${selectedSurah.name} ${selectedSurah.nameArabic ? `(${selectedSurah.nameArabic})` : ''}` 
                    : 'Select Surah'}
                </span>
                <span className="dropdown-icon">▼</span>
              </div>
              {formErrors.surah && <div className="error-text">{formErrors.surah}</div>}
            </div>
            
            {/* Surah Dropdown */}
            {showSurahDropdown && (
              <>
                <div className="dropdown-overlay" onClick={handleOutsideClick}></div>
                <div className="dropdown-container">
                  <div className="search-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search surahs..."
                      value={searchText}
                      onChange={handleSurahSearch}
                      autoFocus
                    />
                  </div>
                  <div className="dropdown-list">
                    {surahOptions
                      .filter(surah =>
                        surah.name.toLowerCase().includes(searchText.toLowerCase()) ||
                        (surah.nameArabic && surah.nameArabic.includes(searchText))
                      )
                      .map((surah) => (
                        <div
                          key={`surah-${surah.id}`}
                          className="dropdown-item"
                          onClick={() => handleSelectSurah(surah)}
                        >
                          {surah.name} {surah.nameArabic ? `(${surah.nameArabic})` : ''}
                        </div>
                      ))
                    }
                  </div>
                </div>
              </>
            )}
            
            {/* Verse Range */}
            <div className="verse-range">
              <div className="form-group half-width">
                <label htmlFor="fromVerse">From Verse</label>
                <input
                  type="number"
                  id="fromVerse"
                  name="fromVerse"
                  value={fromVerse}
                  onChange={handleVerseInputChange}
                  className={formErrors.fromVerse ? 'error' : ''}
                  min="1"
                  max={selectedSurah ? QuranService.getVerseCount(selectedSurah.id) : '999'}
                />
                {formErrors.fromVerse && <div className="error-text">{formErrors.fromVerse}</div>}
              </div>
              
              <div className="form-group half-width">
                <label htmlFor="toVerse">To Verse</label>
                <input
                  type="number"
                  id="toVerse"
                  name="toVerse"
                  value={toVerse}
                  onChange={handleVerseInputChange}
                  className={formErrors.toVerse ? 'error' : ''}
                  min="1"
                  max={selectedSurah ? QuranService.getVerseCount(selectedSurah.id) : '999'}
                />
                {formErrors.toVerse && <div className="error-text">{formErrors.toVerse}</div>}
              </div>
            </div>
            
            {/* Preview Button */}
            <button
              type="button"
              className="preview-button"
              onClick={handlePreviewVerses}
              disabled={!selectedSurah || !fromVerse || !toVerse}
            >
              Preview Verses & Teaching Tips
            </button>
          </div>
          
          {/* Class and Students Section */}
          <div className="form-section">
            <h2>Assign To</h2>
            
            {/* Student Selection */}
            <div className="form-group">
              <label>Students</label>
              <div 
                className={`dropdown-field ${formErrors.students ? 'error' : ''}`}
                onClick={toggleStudentDropdown}
              >
                <span className={selectedStudents.length > 0 ? 'selected-text' : 'placeholder-text'}>
                  {selectedStudents.length > 0 
                    ? `${selectedStudents.length} student${selectedStudents.length > 1 ? 's' : ''} selected` 
                    : 'Select Students'}
                </span>
                <span className="dropdown-icon">▼</span>
              </div>
              {formErrors.students && <div className="error-text">{formErrors.students}</div>}
            </div>
            
            {/* Students Dropdown */}
            {showStudentsDropdown && (
              <>
                <div className="dropdown-overlay" onClick={handleOutsideClick}></div>
                <div className="dropdown-container students-dropdown">
                  {students.length === 0 ? (
                    <div className="empty-dropdown">
                      No students found for this class
                    </div>
                  ) : (
                    <div className="dropdown-list">
                      {students.map(student => (
                        <div
                          key={`student-${student.id}`}
                          className={`dropdown-item student-item ${
                            selectedStudents.some(s => s.id === student.id) ? 'selected' : ''
                          }`}
                          onClick={() => handleToggleStudentSelection(student)}
                        >
                          <span>{student.name}</span>
                          {selectedStudents.some(s => s.id === student.id) && (
                            <span className="checkmark">✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Selected Students Pills */}
            {selectedStudents.length > 0 && (
              <div className="selected-items-container">
                {selectedStudents.map((student) => (
                  <div 
                    key={`selected-${student.id}`} 
                    className="selected-item-pill"
                  >
                    <span>{student.name}</span>
                    <button 
                      type="button" 
                      className="remove-pill-btn"
                      onClick={() => handleToggleStudentSelection(student)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Due Date Section */}
          <div className="form-section">
            <h2>Due Date</h2>
            <div className="form-group">
              <input
                type="date"
                id="dueDate"
                value={selectedDate}
                onChange={handleDateChange}
                className={formErrors.dueDate ? 'error' : ''}
                min={new Date().toISOString().split('T')[0]}
              />
              {formErrors.dueDate && <div className="error-text">{formErrors.dueDate}</div>}
            </div>
          </div>
          
          {/* Enrichment Activities */}
          <div className="form-section">
            <h2>Enrichment Activities</h2>
            <div className="form-group switch-container">
              <label htmlFor="matchingQuiz">Include Ayats Meaning Quiz</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="matchingQuiz"
                  checked={matchingQuiz}
                  onChange={handleMatchingQuizToggle}
                />
                <label htmlFor="matchingQuiz" className="switch-label"></label>
              </div>
            </div>
          </div>
          
          {/* Form Actions */}
          <div className="form-actions">
            <Link to="/assignments" className="cancel-btn">
              Cancel
            </Link>
            
            {/* Delete Button (Only show in edit mode) */}
            {isEditMode && (
              <button
                type="button"
                className="delete-btn"
                onClick={handleDeleteAssignment}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Assignment'}
              </button>
            )}
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Assignment' : 'Create Assignment')}
            </button>
          </div>
        </form>
      </div>
      
      {/* Verse Preview Modal */}
      {showVersePreview && (
        <div className="verse-preview-modal">
          <VersePreview
            verses={verses}
            teachingTips={teachingTips}
            loading={loadingVerses}
            onClose={() => setShowVersePreview(false)}
          />
        </div>
      )}
    </div>
  );
};

export default CreateAssignmentPage;